import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Percent, Building2, Trash2, PlusCircle, MessageSquare } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

/**
 * Phase 4 — Super Admin Business Features console.
 *  1. Advance-payment discount configuration
 *  2. Hospital package category (create + list + delete)
 *  3. Public feedback moderation queue
 */
export default function SuperAdminBusiness() {
    const [discount, setDiscount] = useState({ enabled: false, percent: 0, valid_from: "", valid_until: "" });
    const [savingDiscount, setSavingDiscount] = useState(false);
    const [pkgs, setPkgs] = useState([]);
    const [newPkg, setNewPkg] = useState({ code: "", name: "", tagline: "", description: "", price_inr: 0, amc_inr: 0, advance_pct: 50, feature_count: 0 });
    const [creatingPkg, setCreatingPkg] = useState(false);
    const [feedback, setFeedback] = useState([]);
    const [schedulerStatus, setSchedulerStatus] = useState(null);

    const load = useCallback(async () => {
        try {
            const [d, p, f, s] = await Promise.all([
                api.get("/super-admin/discount"),
                api.get("/packages"),
                api.get("/admin/feedback?page=1&page_size=50"),
                api.get("/super-admin/reminders/status").catch(() => ({ data: null })),
            ]);
            setDiscount({ ...d.data, valid_from: d.data.valid_from || "", valid_until: d.data.valid_until || "" });
            setPkgs(p.data || []);
            setFeedback(f.data.items || []);
            setSchedulerStatus(s.data || null);
        } catch (e) {
            toast.error(errMsg(e, "Failed to load"));
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function saveDiscount() {
        setSavingDiscount(true);
        try {
            const payload = { ...discount, percent: Number(discount.percent) };
            if (!payload.valid_from) delete payload.valid_from;
            if (!payload.valid_until) delete payload.valid_until;
            await api.put("/super-admin/discount", payload);
            toast.success("Discount config saved.");
            load();
        } catch (e) {
            toast.error(errMsg(e, "Save failed"));
        } finally {
            setSavingDiscount(false);
        }
    }

    async function createHospitalPkg() {
        setCreatingPkg(true);
        try {
            const payload = {
                ...newPkg,
                price_inr: Number(newPkg.price_inr),
                amc_inr: Number(newPkg.amc_inr),
                advance_pct: Number(newPkg.advance_pct),
                feature_count: Number(newPkg.feature_count),
            };
            await api.post("/super-admin/packages/hospital", payload);
            toast.success("Hospital package created.");
            setNewPkg({ code: "", name: "", tagline: "", description: "", price_inr: 0, amc_inr: 0, advance_pct: 50, feature_count: 0 });
            load();
        } catch (e) {
            toast.error(errMsg(e, "Create failed"));
        } finally {
            setCreatingPkg(false);
        }
    }

    async function deleteHospitalPkg(code) {
        if (!window.confirm(`Delete hospital package '${code}'?`)) return;
        try {
            await api.delete(`/super-admin/packages/hospital/${code}`);
            toast.success("Package deleted.");
            load();
        } catch (e) {
            toast.error(errMsg(e, "Delete failed"));
        }
    }

    async function moderateFeedback(id, status) {
        try {
            await api.post(`/admin/feedback/${id}/moderate`, { status });
            toast.success(`Feedback ${status}.`);
            load();
        } catch (e) {
            toast.error(errMsg(e, "Moderate failed"));
        }
    }

    async function toggleTestimonial(id, on) {
        try {
            await api.post(`/admin/feedback/${id}/testimonial`, { is_testimonial: on });
            toast.success(on ? "Promoted to testimonial." : "Removed from testimonials.");
            load();
        } catch (e) {
            toast.error(errMsg(e, "Toggle failed"));
        }
    }

    const hospitalPkgs = pkgs.filter((p) => p.category === "hospital");

    return (
        <AdminShell>
            <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="super-business-page">
                <div>
                    <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">Business</div>
                    <h1 className="text-2xl font-bold text-[#123B5D]">Discounts · Hospital packages · Feedback</h1>
                </div>

                {/* Discount config */}
                <Card className="border-[#123B5D]/10" data-testid="discount-card">
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2"><Percent size={16} className="text-[#0F766E]" /> Advance-payment discount</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <p className="text-xs text-[#17212B]/60">
                            Applies when a doctor chooses to pay 100% upfront (instead of the default 50/50).
                            Shown on the pricing page and checkout as a savings badge.
                        </p>
                        <div className="flex items-center gap-3">
                            <Switch checked={!!discount.enabled} onCheckedChange={(v) => setDiscount({ ...discount, enabled: !!v })} data-testid="discount-enabled" />
                            <span className="text-sm text-[#17212B]">Enabled</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                                <Label>Discount %</Label>
                                <Input type="number" min={0} max={90} value={discount.percent}
                                       onChange={(e) => setDiscount({ ...discount, percent: e.target.value })}
                                       data-testid="discount-percent" />
                            </div>
                            <div>
                                <Label>Valid from (optional)</Label>
                                <Input type="date" value={discount.valid_from || ""} onChange={(e) => setDiscount({ ...discount, valid_from: e.target.value })} data-testid="discount-valid-from" />
                            </div>
                            <div>
                                <Label>Valid until (optional)</Label>
                                <Input type="date" value={discount.valid_until || ""} onChange={(e) => setDiscount({ ...discount, valid_until: e.target.value })} data-testid="discount-valid-until" />
                            </div>
                        </div>
                        <Button onClick={saveDiscount} disabled={savingDiscount} data-testid="discount-save">
                            <Save size={13} /> {savingDiscount ? "Saving…" : "Save"}
                        </Button>
                    </CardContent>
                </Card>

                {/* Hospital packages */}
                <Card className="border-[#123B5D]/10" data-testid="hospital-pkgs-card">
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2"><Building2 size={16} className="text-[#0F766E]" /> Hospital packages</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {hospitalPkgs.length ? (
                            <ul className="divide-y">
                                {hospitalPkgs.map((p) => (
                                    <li key={p.code} className="py-2 flex justify-between items-center" data-testid={`hosp-pkg-${p.code}`}>
                                        <div>
                                            <div className="font-semibold text-[#123B5D]">{p.name} <span className="text-xs text-[#17212B]/50">· {p.code}</span></div>
                                            <div className="text-xs text-[#17212B]/60">₹{p.price_inr.toLocaleString()} · AMC ₹{p.amc_inr.toLocaleString()} · {p.feature_count} features</div>
                                        </div>
                                        <Button variant="outline" size="sm" onClick={() => deleteHospitalPkg(p.code)} data-testid={`hosp-pkg-delete-${p.code}`}>
                                            <Trash2 size={13} />
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-xs text-[#17212B]/50">No hospital packages yet.</p>
                        )}

                        <div className="border-t pt-4 mt-2">
                            <div className="text-xs uppercase tracking-widest text-[#17212B]/50 mb-2">Create new</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Input placeholder="code (a-z, _-)" value={newPkg.code} onChange={(e) => setNewPkg({ ...newPkg, code: e.target.value })} data-testid="new-hosp-code" />
                                <Input placeholder="Name" value={newPkg.name} onChange={(e) => setNewPkg({ ...newPkg, name: e.target.value })} data-testid="new-hosp-name" />
                                <Input placeholder="Tagline" value={newPkg.tagline} onChange={(e) => setNewPkg({ ...newPkg, tagline: e.target.value })} data-testid="new-hosp-tagline" />
                                <Input type="number" placeholder="Price INR" value={newPkg.price_inr} onChange={(e) => setNewPkg({ ...newPkg, price_inr: e.target.value })} data-testid="new-hosp-price" />
                                <Input type="number" placeholder="AMC INR" value={newPkg.amc_inr} onChange={(e) => setNewPkg({ ...newPkg, amc_inr: e.target.value })} data-testid="new-hosp-amc" />
                                <Input type="number" placeholder="Feature count" value={newPkg.feature_count} onChange={(e) => setNewPkg({ ...newPkg, feature_count: e.target.value })} data-testid="new-hosp-features" />
                                <Textarea rows={2} placeholder="Description" value={newPkg.description} onChange={(e) => setNewPkg({ ...newPkg, description: e.target.value })} data-testid="new-hosp-desc" className="md:col-span-2" />
                            </div>
                            <Button onClick={createHospitalPkg} disabled={creatingPkg || !newPkg.code || !newPkg.name} className="mt-3" data-testid="new-hosp-create">
                                <PlusCircle size={13} /> {creatingPkg ? "Creating…" : "Create hospital package"}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Scheduler status */}
                <Card className="border-[#123B5D]/10" data-testid="scheduler-status-card">
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2"><Percent size={16} className="text-[#0F766E]" /> AMC + Onboarding Reminder Scheduler</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {schedulerStatus ? (
                            <div className="text-sm space-y-2">
                                <div>Enabled: <b className={schedulerStatus.scheduler_enabled ? "text-[#15803D]" : "text-[#DC2626]"}>{String(schedulerStatus.scheduler_enabled)}</b></div>
                                <div>Task running: <b className={schedulerStatus.task_running ? "text-[#15803D]" : "text-[#D97706]"}>{String(schedulerStatus.task_running)}</b></div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                                    <div className="bg-[#F5F8FA] p-3 rounded-md">
                                        <div className="text-xs text-[#0F766E] font-semibold uppercase mb-1">Last AMC scan</div>
                                        <pre className="text-xs overflow-x-auto">{JSON.stringify(schedulerStatus.last_amc_scan, null, 2)}</pre>
                                    </div>
                                    <div className="bg-[#F5F8FA] p-3 rounded-md">
                                        <div className="text-xs text-[#0F766E] font-semibold uppercase mb-1">Last onboarding-nudge scan</div>
                                        <pre className="text-xs overflow-x-auto">{JSON.stringify(schedulerStatus.last_onboarding_nudge_scan, null, 2)}</pre>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <p className="text-xs text-[#17212B]/60">No status yet.</p>
                        )}
                    </CardContent>
                </Card>

                {/* Feedback moderation queue */}
                <Card className="border-[#123B5D]/10" data-testid="feedback-queue-card">
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2"><MessageSquare size={16} className="text-[#0F766E]" /> Public feedback queue</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {feedback.length ? (
                            <ul className="divide-y">
                                {feedback.map((f) => (
                                    <li key={f.id} className="py-3 space-y-1" data-testid={`fb-${f.id}`}>
                                        <div className="flex justify-between items-center">
                                            <div className="text-sm font-semibold text-[#123B5D]">{f.name} · Rating {f.rating}/5</div>
                                            <div className="text-xs text-[#17212B]/50">{(f.created_at || "").split("T")[0]} · {f.status}</div>
                                        </div>
                                        <p className="text-sm text-[#17212B]/80">{f.message}</p>
                                        <div className="flex gap-2">
                                            <Button size="sm" variant="outline" onClick={() => moderateFeedback(f.id, "reviewed")}>Mark reviewed</Button>
                                            <Button size="sm" variant="outline" onClick={() => moderateFeedback(f.id, "hidden")}>Hide</Button>
                                            {f.rating >= 4 ? (
                                                <Button
                                                    size="sm"
                                                    variant={f.is_testimonial ? "default" : "outline"}
                                                    onClick={() => toggleTestimonial(f.id, !f.is_testimonial)}
                                                    data-testid={`fb-testimonial-${f.id}`}
                                                >
                                                    {f.is_testimonial ? "★ Testimonial ON" : "Promote to testimonial"}
                                                </Button>
                                            ) : (
                                                <span className="text-xs text-[#17212B]/40 self-center">Only 4-5★ can be promoted</span>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-xs text-[#17212B]/50">No feedback yet.</p>
                        )}
                    </CardContent>
                </Card>
            </div>
        </AdminShell>
    );
}
